/**
 * "I have a question" on the plan page (Treatment Plan spec v1.4, S-07).
 *
 * Topic, then question, then the instant answer from the in-house question bank,
 * then "Yes, back to my plan" or "No, ask my care team". Asking opens a short
 * screen with the question already filled in, room for the patient's own words,
 * and how they would like to hear back. All of it is in the app, never Typeform.
 * The answers they get show under the plan.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { portalRequest } from '../../lib/portal';
import { Card, formatPortalDate, usePortalQuery } from './shell';
import { ArrowLeft } from './icons';

interface Menu {
  nextLabDraw: string | null;
  topics: Array<{ key: string; label: string; questions: Array<{ id: string; question: string; answer: string | null }> }>;
}
interface AskedQuestion {
  id: string;
  question: string;
  yourWords: string | null;
  askedAt: string;
  status: 'open' | 'answered' | 'closed';
  answer: string | null;
  answerChannel: string | null;
  answeredAt: string | null;
}

export const MUTED = '#5b6168';
export const LINE = '#e6e3dc';
export const FOREST_SOFT = '#e6f0ea';
const event = (planId: string, type: string, detail: Record<string, unknown>) =>
  void portalRequest(`/plans/${planId}/events`, { method: 'POST', body: { type, detail } }).catch(() => undefined);

/** Board 5: each topic's glyph and the words under it. */
const TOPIC_LOOK: Record<string, { glyph: string; sub: string }> = {
  cost: { glyph: '$', sub: "Price, what's included, charges" },
  meds: { glyph: 'Rx', sub: 'Why these, side effects, how to take' },
  time: { glyph: 'mo', sub: 'Plan length, results, stopping' },
  start: { glyph: 'go', sub: 'Shipping, storage, first dose' },
  labs: { glyph: 'lab', sub: 'Why my results led to this plan' },
  other: { glyph: '...', sub: 'Write to your care team' },
};
const CONTACT_OPTIONS = [
  ['text', 'Text'],
  ['call', 'Call me'],
  ['visit', '10-min visit'],
] as const;

export function Chevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

/** One full phone screen, as board 5 draws each step: a header with a back arrow, the body, and a footer. */
export function Screen({ title, onBack, children, footer, testId }: { title: string; onBack: () => void; children: ReactNode; footer?: ReactNode; testId?: string }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-[#f7f6f2]" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex h-full w-full max-w-[430px] flex-col" data-testid={testId}>
        <div className="flex shrink-0 items-center gap-[10px] border-b bg-white px-5 pb-3 pt-[max(16px,env(safe-area-inset-top))]" style={{ borderColor: LINE }}>
          <button type="button" onClick={onBack} aria-label="Back" className="flex" style={{ color: 'var(--forest)' }}>
            <ArrowLeft size={22} />
          </button>
          <span className="text-[17px] font-semibold" style={{ color: '#1c2b3a' }}>{title}</span>
        </div>
        <div className="flex-1 overflow-y-auto px-[18px] pb-4 pt-3">{children}</div>
        {footer && <div className="shrink-0 border-t bg-white px-[18px] pb-[calc(18px+env(safe-area-inset-bottom))] pt-3" style={{ borderColor: LINE }}>{footer}</div>}
      </div>
    </div>
  );
}

export const primary = 'flex h-[50px] w-full items-center justify-center rounded-[12px] bg-forest text-[16px] font-semibold text-white disabled:opacity-50';
export const secondary = 'flex h-[48px] w-full items-center justify-center rounded-[12px] border bg-white text-[15px] font-semibold';

export function QuestionSheet({ planId, clinicName, onClose }: { planId: string; clinicName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const menu = usePortalQuery<Menu>(`plan-questions-menu-${planId}`, `/plans/${planId}/questions/menu`);
  const [topicKey, setTopicKey] = useState<string | null>(null);
  /** A bank question, or 'free' for "Ask something else about this". */
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [words, setWords] = useState('');
  const [contact, setContact] = useState<'text' | 'call' | 'visit'>('text');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const topic = menu.data?.topics.find((t) => t.key === topicKey) ?? null;
  const free = questionId === 'free';
  const question = free ? null : topic?.questions.find((q) => q.id === questionId) ?? null;

  useEffect(() => {
    if (question?.answer) event(planId, 'instant_answer_viewed', { question: question.id });
  }, [planId, question?.id, question?.answer]);

  const send = useMutation({
    mutationFn: (resolved: boolean) =>
      portalRequest(`/plans/${planId}/questions`, {
        method: 'POST',
        body: free
          ? { questionId: null, topicKey, resolved: false, patientText: words || null, contactPref: contact }
          : { questionId, resolved, patientText: resolved ? null : words || null, contactPref: resolved ? null : contact },
      }),
    onSuccess: (_r, resolved) => {
      void qc.invalidateQueries({ queryKey: ['portal'] });
      if (resolved) onClose();
      else setSent(true);
    },
  });

  const pickTopic = (key: string) => {
    setTopicKey(key);
    event(planId, 'question_topic_opened', { topic: key });
    // "Something else" has nothing to pick: it goes straight to the care team.
    const only = menu.data?.topics.find((t) => t.key === key)?.questions;
    if (key === 'other' && only?.length === 1) {
      setQuestionId(only[0]!.id);
      setAsking(true);
    }
  };

  if (sent) {
    return (
      <Screen title="Ask your care team" onBack={onClose} testId="question-sent" footer={<button type="button" className={primary} onClick={onClose}>Back to my plan</button>}>
        <h2 className="mt-3 font-display text-[24px] font-semibold leading-snug" style={{ color: '#1c2b3a' }}>Sent to your care team</h2>
        <p className="mt-2 text-[14.5px] leading-normal" style={{ color: MUTED }}>
          {clinicName} will answer here, on your plan. Your plan stays saved. You can accept any time.
        </p>
      </Screen>
    );
  }

  if (asking && (question || free || topicKey === 'other')) {
    const label = topic?.label ?? 'Something else';
    return (
      <Screen
        title="Ask your care team"
        onBack={() => (topicKey === 'other' ? (setAsking(false), setQuestionId(null), setTopicKey(null)) : setAsking(false))}
        testId="ask-screen"
        footer={
          <>
            {send.error && <p className="mb-2 text-[13px] text-alert" role="alert">{send.error instanceof Error ? send.error.message : 'Could not send'}</p>}
            <button type="button" className={primary} disabled={send.isPending || ((free || question?.id === 'GQ-O01') && !words.trim())} onClick={() => send.mutate(false)}>
              Send to my care team
            </button>
          </>
        }
      >
        <div className="flex flex-wrap gap-2">
          <span className="rounded-[6px] px-2 py-1 text-[12px] font-semibold" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>{label}</span>
          {question && question.id !== 'GQ-O01' && <span className="rounded-[6px] px-2 py-1 text-[12px]" style={{ background: '#eeede8', color: '#1c2b3a' }}>{question.question}</span>}
        </div>
        <p className="mt-4 text-[14px] font-semibold" style={{ color: '#1c2b3a' }}>{free || question?.id === 'GQ-O01' ? 'Your question' : 'Anything to add? (optional)'}</p>
        <textarea
          aria-label="Anything to add, in your own words"
          className="mt-2 h-[100px] w-full resize-none rounded-[12px] border bg-white px-3 py-[10px] text-[14px] outline-none focus:border-forest"
          style={{ borderColor: '#d8d3c8' }}
          maxLength={2000}
          value={words}
          onChange={(e) => setWords(e.target.value)}
        />
        <p className="mt-4 text-[14px] font-semibold" style={{ color: '#1c2b3a' }}>How should we reach you?</p>
        <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label="How should we reach you">
          {CONTACT_OPTIONS.map(([value, text]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={contact === value}
              onClick={() => setContact(value)}
              className={`h-11 rounded-[10px] text-[14px] ${contact === value ? 'border-2 font-semibold' : 'border'}`}
              style={contact === value ? { borderColor: 'var(--forest)', background: FOREST_SOFT, color: '#1b3f35' } : { borderColor: '#d8d3c8', background: '#fff' }}
            >
              {text}
            </button>
          ))}
        </div>
        <p className="mt-4 rounded-[12px] px-3 py-2.5 text-[13px] leading-normal" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>
          Your care team sees your plan and this question, so you won't have to explain it twice.
        </p>
        <p className="mt-3 text-[12.5px]" style={{ color: MUTED }}>Your plan stays saved. You can accept any time.</p>
      </Screen>
    );
  }

  if (question) {
    return (
      <Screen
        title="Your answer"
        onBack={() => setQuestionId(null)}
        testId="instant-answer"
        footer={
          question.answer ? (
            <div className="flex flex-col gap-[10px]">
              <p className="text-center text-[14px] font-semibold" style={{ color: '#1c2b3a' }}>Did this answer your question?</p>
              <button type="button" className={primary} disabled={send.isPending} onClick={() => send.mutate(true)}>Yes, back to my plan</button>
              <button type="button" className={secondary} style={{ borderColor: '#1c2b3a', color: '#1c2b3a' }} onClick={() => setAsking(true)}>No, ask my care team</button>
            </div>
          ) : (
            <button type="button" className={primary} onClick={() => setAsking(true)}>Ask my care team</button>
          )
        }
      >
        <h2 className="font-display text-[23px] font-semibold leading-snug" style={{ color: '#1c2b3a' }}>{question.question}</h2>
        <div className="mt-3 rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }}>
          <p className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: '#1c2b3a' }}>From your plan</p>
          <p className="mt-2 text-[14.5px] leading-[1.55]" style={{ color: '#1c2b3a' }}>{question.answer ?? 'Your care team will answer this one.'}</p>
          {question.answer && menu.data?.nextLabDraw && (
            <div className="mt-3 flex justify-between border-t pt-2.5 text-[13.5px]" style={{ borderColor: LINE }}>
              <span style={{ color: MUTED }}>Next lab draw</span>
              <b style={{ color: '#1c2b3a' }}>{menu.data.nextLabDraw}</b>
            </div>
          )}
        </div>
        {question.answer && <p className="mt-2 text-[12.5px]" style={{ color: MUTED }}>Written by your clinic, filled in from your plan.</p>}
      </Screen>
    );
  }

  if (topic) {
    return (
      <Screen title={topic.label} onBack={() => setTopicKey(null)} testId="topic-questions">
        <p className="text-[12.5px]" style={{ color: MUTED }}>Common questions</p>
        <div className="mt-2 flex flex-col gap-[10px]">
          {topic.questions.map((q) => (
            <button key={q.id} type="button" onClick={() => setQuestionId(q.id)} className="flex min-h-[52px] items-center justify-between gap-2 rounded-[12px] border bg-white px-4 py-3 text-left text-[14.5px]" style={{ borderColor: LINE, color: '#1c2b3a' }}>
              {q.question}
              <Chevron />
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setQuestionId('free');
              setAsking(true);
            }}
            className="flex min-h-[52px] items-center justify-between gap-2 rounded-[12px] border border-dashed px-4 py-3 text-left text-[14.5px]"
            style={{ borderColor: '#c9c4b8', color: '#1c2b3a' }}
          >
            Ask something else about this
            <Chevron />
          </button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen title="Ask about your plan" onBack={onClose} testId="question-topics">
      <h2 className="font-display text-[24px] font-semibold leading-snug" style={{ color: '#1c2b3a' }}>What is your question about?</h2>
      <p className="mt-1 text-[13.5px] leading-normal" style={{ color: MUTED }}>Pick one. You get an answer right away, and your care team can help with anything else.</p>
      {menu.isLoading && <p className="mt-3 text-[13px]" style={{ color: MUTED }}>Loading</p>}
      {menu.error && <p className="mt-3 text-[13px] text-alert">{menu.error instanceof Error ? menu.error.message : 'Could not load the questions'}</p>}
      <div className="mt-3 flex flex-col gap-[10px]">
        {menu.data?.topics.map((t) => {
          const look = TOPIC_LOOK[t.key] ?? { glyph: '?', sub: '' };
          return (
            <button key={t.key} type="button" onClick={() => pickTopic(t.key)} className="flex min-h-[64px] items-center gap-3 rounded-[12px] border bg-white px-3 py-3 text-left" style={{ borderColor: LINE }}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] text-[13px] font-semibold" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>{look.glyph}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold" style={{ color: '#1c2b3a' }}>{t.label}</span>
                {look.sub && <span className="block text-[12.5px]" style={{ color: MUTED }}>{look.sub}</span>}
              </span>
              <Chevron />
            </button>
          );
        })}
      </div>
    </Screen>
  );
}

/** The questions this patient asked about the plan, and the answers. */
export function PlanAnswers({ planId }: { planId: string }) {
  const asked = usePortalQuery<{ questions: AskedQuestion[] }>(`plan-questions-${planId}`, `/plans/${planId}/questions`);
  const rows = asked.data?.questions ?? [];
  if (rows.length === 0) return null;
  return (
    <Card className="px-[14px] py-[12px]" data-testid="plan-answers">
      <p className="text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: '#1c2b3a' }}>Your questions</p>
      <ul className="mt-2 flex flex-col gap-3">
        {rows.map((q) => (
          <li key={q.id} className="text-[14px] leading-normal">
            <p className="font-semibold" style={{ color: '#1c2b3a' }}>{q.question}</p>
            {q.yourWords && <p style={{ color: MUTED }}>"{q.yourWords}"</p>}
            {q.answer ? (
              <p className="mt-1 rounded-[10px] px-3 py-2" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>
                {q.answer}
                <span className="mt-1 block text-[12px]" style={{ color: MUTED }}>Answered {formatPortalDate(q.answeredAt)}</span>
              </p>
            ) : (
              <p className="mt-1 text-[13px]" style={{ color: MUTED }}>We're answering your question.</p>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
